export const LOG_LEVELS = ['error', 'warn', 'info', 'debug'] as const;

export type LogLevel = (typeof LOG_LEVELS)[number];

export interface LogConfig {
  enabled: boolean;
  level: LogLevel;
  labels: Record<string, LogLevel>;
}

export interface Logger {
  error(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  info(...args: unknown[]): void;
  debug(...args: unknown[]): void;
}

const STORAGE_KEY = 'ns-logging';

const isLevel = (value: unknown): value is LogLevel => LOG_LEVELS.includes(value as LogLevel);

function readConfig(): LogConfig {
  const config: LogConfig = { enabled: true, level: 'info', labels: {} };
  try {
    const stored = JSON.parse(globalThis.localStorage?.getItem(STORAGE_KEY) ?? 'null');
    if (typeof stored?.enabled === 'boolean') config.enabled = stored.enabled;
    if (isLevel(stored?.level)) config.level = stored.level;
    Object.entries(stored?.labels ?? {}).forEach(([label, level]) => {
      if (isLevel(level)) config.labels[label] = level;
    });
  } catch {
    // Unreadable or unavailable storage leaves the defaults.
  }
  return config;
}

const config = readConfig();

function saveConfig() {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Settings then last only as long as the page.
  }
}

export function shouldLog(current: LogConfig, label: string, level: LogLevel): boolean {
  if (level === 'error') return true;
  if (!current.enabled) return false;

  const threshold = current.labels[label] ?? current.level;
  return LOG_LEVELS.indexOf(level) <= LOG_LEVELS.indexOf(threshold);
}

export function createLogger(label: string): Logger {
  const prefix = `[${label}]`;
  const print =
    (level: LogLevel, write: (...args: unknown[]) => void) =>
    (...args: unknown[]) => {
      if (shouldLog(config, label, level)) write(prefix, ...args);
    };

  // Looked up per call, so a console patched later (a test spy, devtools) still sees every call.
  return {
    error: print('error', (...args) => console.error(...args)),
    warn: print('warn', (...args) => console.warn(...args)),
    info: print('info', (...args) => console.info(...args)),
    debug: print('debug', (...args) => console.debug(...args)),
  };
}

export function isLoggingEnabled(): boolean {
  return config.enabled;
}

export function setLoggingEnabled(enabled: boolean) {
  config.enabled = enabled;
  saveConfig();
}

export function getLogLevel(label?: string): LogLevel {
  return (label !== undefined && config.labels[label]) || config.level;
}

export function setLogLevel(level: LogLevel, label?: string) {
  if (label === undefined) config.level = level;
  else config.labels[label] = level;
  saveConfig();
}

export function clearLogLevel(label: string) {
  delete config.labels[label];
  saveConfig();
}
